import type { AppDefinition } from '@/types/app';
import { isWebApp, newWebAppId } from '@/webapp/webAppRecord';

/** Said instead of copying an app whose document cannot come with it. */
export const DOCUMENT_COPY_REFUSED =
  'This app reads a document stored with it, and a copy can’t take the document along yet. '
  + 'To make another app from the same document, start a new app and add the document again.';

/**
 * Why this app cannot be duplicated, or null when it can.
 *
 * A web app built around a long text (`textAsset`) reads it with `S.asset(name)` from the text
 * store under ITS OWN catalog id, and the text is uploaded there once, by the build. A copy gets
 * a catalog id of its own the first time it publishes — which every edit does — and nothing
 * uploads the text again. So the copy's page asked for a text that was never stored under its
 * id, and showed no text at all, with nothing saying why. Refused here, before any store writes
 * a row, rather than handing the owner a copy that breaks the moment it goes live.
 */
export function duplicateRefusal(definition: AppDefinition | null | undefined): string | null {
  return definition && isWebApp(definition) && definition.textAsset ? DOCUMENT_COPY_REFUSED : null;
}

/**
 * The definition a duplicate starts from: the app itself, and nothing that ties it to the
 * original's catalog entry or page. Every ProjectStore's `duplicate` stores this, and the
 * conformance suite (`__tests__/projectStoreConformance.ts`) holds each of them to it.
 *
 * A duplicate is a NEW app that has never been published. Copied whole, it kept the original's
 * `hostedAppId`, and a web app's edits publish themselves to that id (`hostLatest` on the phone,
 * `publishLatest` in the browser) — so editing the copy replaced the ORIGINAL's live page. The
 * address has to go too, not only the id: a record with a `webUrl` and no id is matched to the
 * catalog app serving that address before it is published (`appAtAddress` in saddleHost), which
 * would find the original's again. And the records of what went up there — the phone's
 * `publishedFingerprint`, the browser's `publishedSourceHash` — describe the original's page,
 * not a page this app has. The same list a remix drops (`buildRemix`), for the same reason.
 *
 * A web app gets its own `id` as well, minted as a new web app's is. That id is the localStorage
 * namespace baked into its page (`window.__SADDLE_APP__`), so a copy that kept it would, once
 * published, read and write the saved data of the original's visitors on the shared origin.
 * Other runtimes keep theirs: an engine app's data is stored per project row, not per this id.
 *
 * Its `editHistory` goes too. Those are the ORIGINAL's earlier versions: Undo on a fresh copy
 * would put back a change the copy never had — and after the first publish, onto the copy's own
 * page — while the copy's row carried five extra app bodies it has no use for.
 *
 * Kept, because it IS the app: the source, the plan, the look and the owner's settings — and
 * `unfinished`. A copy of an app that was never built has not been built either, and a copy
 * without the mark would read as finished, and publishable, with no app in it.
 *
 * An app built around a document is refused instead: see `duplicateRefusal`.
 */
export function duplicateDefinition(definition: AppDefinition, name: string): AppDefinition {
  const refused = duplicateRefusal(definition);
  if (refused) throw new Error(refused);
  // A deep copy: the stores already keep definitions from aliasing a caller's object.
  const copy = JSON.parse(JSON.stringify(definition)) as AppDefinition & { publishedSourceHash?: string };
  delete copy.webUrl;
  delete copy.hostedAppId;
  delete copy.publishedFingerprint;
  delete copy.publishedSourceHash;
  delete copy.editHistory;
  if (isWebApp(copy)) copy.id = newWebAppId(name);
  return copy;
}
